import {
  ColorClass,
  Icon,
  IconIdentifier,
  ProgressBar,
  ProgressBarLayout,
} from '@aus-platform/design-system';
import { isNil } from 'lodash';
import { useEffect, useState } from 'react';
import { FileType } from 'shared/api';
import type { MultiPartUploadUrls } from 'shared/hooks';
import { getFileUploadStatus, useMultipartUpload } from 'shared/hooks';
import { Converter } from 'shared/utils';
import { type AttachmentFileCardProps } from './types';

export const AttachmentFileCard: React.FC<AttachmentFileCardProps> = ({
  selectedFile,
  onRemoveFile,
  onUploadStatusChange,
  shouldCancelUpload,
}) => {
  // States.
  const [isUploadUrlsInitialized, setIsUploadUrlsInitialized] = useState(false);

  // Variables.
  const { file, id, fileUploadId, isExisting } = selectedFile;

  // Hooks.
  const { progress, setUrls, startUploading, cancelUpload, reset } =
    useMultipartUpload({
      fileType: FileType.Attachment,
    });

  const uploadStatus = getFileUploadStatus(progress);

  // Effects.
  useEffect(() => {
    if (isExisting) {
      onUploadStatusChange(id, false);
      return;
    }

    if (isNil(fileUploadId)) {
      return;
    }

    const multipartUploadUrls: MultiPartUploadUrls = {
      startUploadUrl: `/files/${fileUploadId}/upload-file/`,
      presignedUrl: `/files/${fileUploadId}/presigned-url/`,
      completeUploadUrl: `/files/${fileUploadId}/complete-upload/`,
    };

    setUrls(multipartUploadUrls);
    setIsUploadUrlsInitialized(true);
  }, [fileUploadId, isExisting]);

  useEffect(() => {
    if (isExisting || !isUploadUrlsInitialized) {
      return;
    }

    startUploading(file);
  }, [isExisting, isUploadUrlsInitialized, file]);

  useEffect(() => {
    const isUploading = uploadStatus.isFileUploading || uploadStatus.isFileIdle;

    onUploadStatusChange(id, isUploading);
  }, [id, uploadStatus.isFileUploading, uploadStatus.isFileIdle]);

  useEffect(() => {
    if (!shouldCancelUpload) {
      return;
    }

    const cancelAndCleanupUpload = async () => {
      try {
        if (uploadStatus.isFileUploading || uploadStatus.isFileIdle) {
          await cancelUpload(true);
        }
      } finally {
        cleanupRemovedFile();
      }
    };

    cancelAndCleanupUpload();
  }, [shouldCancelUpload]);

  // Handlers.
  const handleRemoveFile = async () => {
    try {
      await cancelUpload(true);
    } finally {
      cleanupRemovedFile();
    }
  };

  const cleanupRemovedFile = () => {
    reset();
    onUploadStatusChange(id, false);
    onRemoveFile(id, fileUploadId);
  };

  return (
    <div className="dashboard-modal__attachments__file-card">
      <Icon
        identifier={IconIdentifier.File}
        size={20}
        colorClass={ColorClass.Neutral250}
      />

      <div className="dashboard-modal__attachments__file-card__content">
        <div className="dashboard-modal__attachments__file-card__details">
          <span
            className="dashboard-modal__attachments__file-card__name"
            title={file.name}
          >
            {file.name}
          </span>

          <div className="dashboard-modal__attachments__file-card__info">
            <span className="dashboard-modal__attachments__file-card__size">
              {Converter.bytesToSizeConverter(file.size)}
            </span>

            {uploadStatus.isFileUploading && (
              <>
                <ProgressBar
                  layout={ProgressBarLayout.Secondary}
                  now={progress}
                />

                <span className="dashboard-modal__attachments__file-card__progress">
                  {progress} %
                </span>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="dashboard-modal__attachments__file-card__remove-icon">
        <Icon
          identifier={IconIdentifier.CloseCircle}
          size={20}
          colorClass={ColorClass.Red500}
          onClick={handleRemoveFile}
        />
      </div>
    </div>
  );
};
